import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CTASection } from "@/components/CTASection";
import { LinkedText } from "@/components/LinkedText";
import { PhoneFrame } from "@/components/PhoneFrame";
import { getWatchPage } from "@/data/watchPages";

const BASE_URL = "https://masonry-pro.com";

export function WatchPage({ slug }: { slug: string }) {
  const page = getWatchPage(slug);
  if (!page) return null;
  const url = `${BASE_URL}/videos/${page.slug}`;

  const video = (
    <PhoneFrame metal hideNotch className="max-w-[340px]">
      <iframe
        className="absolute inset-0 h-full w-full"
        src={`https://www.youtube.com/embed/${page.videoId}`}
        title={page.h1}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
      />
    </PhoneFrame>
  );

  const copy = (
    <>
      <h2 className="text-2xl md:text-3xl font-bold mb-4">About This Project</h2>
      <div className="space-y-4 text-lg text-muted-foreground">
        {page.about.map((p, i) => (
          <p key={i}><LinkedText text={p} /></p>
        ))}
      </div>
      <h2 className="text-2xl md:text-3xl font-bold mt-10 mb-4">What We Built</h2>
      <ul className="list-disc pl-6 space-y-2 text-lg text-muted-foreground">
        {page.built.map((b, i) => <li key={i}>{b}</li>)}
      </ul>
    </>
  );

  return (
    <>
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.description} />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.description} />
        <link rel="canonical" href={url} />
      </Helmet>
      <Header />
      <nav aria-label="Breadcrumb" className="bg-primary border-b border-primary/80 pt-28 md:pt-32">
        <div className="container mx-auto px-4">
          <ol className="flex flex-wrap items-center gap-2 py-4 text-base">
            <li>
              <Link to="/" className="flex items-center gap-1 text-primary-foreground/70 hover:text-secondary transition-colors">
                <Home className="h-5 w-5" />
                <span className="sr-only">Home</span>
              </Link>
            </li>
            <li className="flex items-center gap-2">
              <ChevronRight className="h-5 w-5 text-primary-foreground/40" />
              <Link to="/videos" className="text-primary-foreground/70 hover:text-secondary transition-colors">Project Videos</Link>
            </li>
            <li className="flex items-center gap-2">
              <ChevronRight className="h-5 w-5 text-primary-foreground/40" />
              <span className="text-secondary font-semibold">{page.h1}</span>
            </li>
          </ol>
        </div>
      </nav>
      <main className="bg-background">
        <section className="py-10 md:py-14">
          <div className="container mx-auto px-4">
            <div className="max-w-[800px] mx-auto">
              <h1 className="text-3xl md:text-5xl font-bold mb-3">{page.h1}</h1>
              <p className="text-lg text-muted-foreground mb-8">{page.intro}</p>
            </div>
            <div className="max-w-5xl mx-auto grid gap-10 lg:grid-cols-[360px_1fr] items-start">
              {video}
              <div>{copy}</div>
            </div>
          </div>
        </section>
        <CTASection />
      </main>
      <Footer />
    </>
  );
}
